import React, { useState } from 'react';
import { NavButton } from './NavButton';

interface DateRangePickerProps {
	startDate: Date | null;
	endDate: Date | null;
	onSelectRange: (start: Date | null, end: Date | null) => void;
	onClose: () => void;
	// Explicit commit for the whole popover (range + date flex together) --
	// picking dates/flex only updates the in-progress selection shown here
	// and in the calling form's trigger buttons; nothing is dispatched as a
	// live search filter until this fires. Split out from onSelectRange
	// because a start-date click is itself a legitimate onSelectRange call
	// without the user being "done" -- there's no single click that
	// unambiguously means "the range is finished".
	onApply: () => void;
	dateFlex?: 'exact' | 'pm3' | 'pm7';
	onDateFlexChange?: (value: 'exact' | 'pm3' | 'pm7') => void;
}

export const DateRangePicker = ({ startDate, endDate, onSelectRange, onClose, onApply, dateFlex, onDateFlexChange }: DateRangePickerProps) => {
	const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
	const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
	const [selectingStart, setSelectingStart] = useState(!startDate);
	const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

	const monthNames = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
	
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	// "+/- N days" widens the highlighted band by N days on each side of the
	// selected (or hovered) range, rendered identically to the exact range —
	// the fuzz window and the exact dates aren't visually distinguished.
	const flexDays = dateFlex === 'pm3' ? 3 : dateFlex === 'pm7' ? 7 : 0;
	const addDays = (date: Date, days: number) => {
		const result = new Date(date);
		result.setDate(result.getDate() + days);
		return result;
	};

	const isRangeStart = (date: Date) => {
		return startDate && date.toDateString() === startDate.toDateString();
	};

	const isRangeEnd = (date: Date) => {
		return endDate && date.toDateString() === endDate.toDateString();
	};

	const isInRange = (date: Date) => {
		if (!startDate || !endDate) return false;
		return date >= addDays(startDate, -flexDays) && date <= addDays(endDate, flexDays);
	};

	const isInHoverRange = (date: Date) => {
		if (!startDate || !hoveredDate || endDate) return false;
		const min = startDate < hoveredDate ? startDate : hoveredDate;
		const max = startDate < hoveredDate ? hoveredDate : startDate;
		return date >= addDays(min, -flexDays) && date <= addDays(max, flexDays);
	};

	const handleDateClick = (date: Date) => {
		if (selectingStart) {
			onSelectRange(date, null);
			setSelectingStart(false);
		} else {
			if (startDate && date < startDate) {
				onSelectRange(date, null);
			} else {
				onSelectRange(startDate, date);
			}
		}
	};

	const renderMonth = (monthOffset: number) => {
		let month = currentMonth + monthOffset;
		let year = currentYear;
		
		if (month > 11) {
			month -= 12;
			year += 1;
		} else if (month < 0) {
			month += 12;
			year -= 1;
		}

		const firstDay = new Date(year, month, 1).getDay();
		const daysInMonth = new Date(year, month + 1, 0).getDate();

		const days: React.ReactElement[] = [];
		for (let i = 0; i < firstDay; i++) {
			days.push(<span key={`empty-${i}`} className="booking-bar__calendar-day booking-bar__calendar-day--empty" />);
		}
		
		for (let day = 1; day <= daysInMonth; day++) {
			const date = new Date(year, month, day);
			const isPast = date < today;
			// Covers three cases as one uniform highlight: the confirmed
			// start/end dates themselves, the exact range between them
			// (padded by flexDays), and the live hover preview before an
			// end date is confirmed (also padded).
			const isHighlighted = isRangeStart(date) || isRangeEnd(date) || isInRange(date) || isInHoverRange(date);

			let className = 'booking-bar__calendar-day';
			if (isPast) className += ' booking-bar__calendar-day--disabled';
			if (isHighlighted) className += ' booking-bar__calendar-day--in-range';
			
			days.push(
				<button
					key={day}
					type="button"
					className={className}
					disabled={isPast}
					onClick={() => handleDateClick(date)}
					onMouseEnter={() => setHoveredDate(date)}
					onMouseLeave={() => setHoveredDate(null)}
				>
					{day}
				</button>
			);
		}

		return (
			<div key={`month-${monthOffset}`} className="booking-bar__calendar-month-grid">
				<div className="booking-bar__calendar-month-name">{monthNames[month]} {year}</div>
				<div className="booking-bar__calendar-weekdays">
					<span>SU</span><span>MO</span><span>TU</span><span>WE</span><span>TH</span><span>FR</span><span>SA</span>
				</div>
				<div className="booking-bar__calendar-days">{days}</div>
			</div>
		);
	};

	const prevMonth = () => {
		if (currentMonth === 0) {
			setCurrentMonth(11);
			setCurrentYear(currentYear - 1);
		} else {
			setCurrentMonth(currentMonth - 1);
		}
	};

	const nextMonth = () => {
		if (currentMonth === 11) {
			setCurrentMonth(0);
			setCurrentYear(currentYear + 1);
		} else {
			setCurrentMonth(currentMonth + 1);
		}
	};

	return (
		<div className="booking-bar__calendar booking-bar__calendar--range">
			<div className="booking-bar__calendar-header">
				<NavButton direction="prev" onClick={prevMonth} color="#2c5375" />
				<span className="booking-bar__calendar-title">
					{selectingStart ? 'Select check-in date' : 'Select check-out date'}
				</span>
				<NavButton direction="next" onClick={nextMonth} color="#2c5375" />
			</div>
			<div className="booking-bar__calendar-months">
				{renderMonth(0)}
				{renderMonth(1)}
			</div>
			<div className="booking-bar__calendar-footer">
				{onDateFlexChange && (
					<div className="booking-bar__date-flex">
						<span className="booking-bar__date-flex-label">Dates</span>
						{/* The options are grouped so they wrap together beneath the
						    label rather than one of them breaking away on its own --
						    see &__date-flex-options in booking-bar.scss. */}
						<div className="booking-bar__date-flex-options">
							<label className="booking-bar__date-flex-option">
								<input
									type="radio"
									name="date_flex"
									value="exact"
									checked={dateFlex === 'exact'}
									onChange={() => onDateFlexChange('exact')}
								/>
								<span>Exact</span>
							</label>
							<label className="booking-bar__date-flex-option">
								<input
									type="radio"
									name="date_flex"
									value="pm3"
									checked={dateFlex === 'pm3'}
									onChange={() => onDateFlexChange('pm3')}
								/>
								<span>+/- 3 days</span>
							</label>
							<label className="booking-bar__date-flex-option">
								<input
									type="radio"
									name="date_flex"
									value="pm7"
									checked={dateFlex === 'pm7'}
									onChange={() => onDateFlexChange('pm7')}
								/>
								<span>+/- 7 days</span>
							</label>
						</div>
					</div>
				)}
				<div className="booking-bar__calendar-actions">
					<button type="button" className="booking-bar__calendar-clear" onClick={() => {
						onSelectRange(null, null);
						setSelectingStart(true);
					}}>
						Clear dates
					</button>
					<button
						type="button"
						className="booking-bar__calendar-apply"
						disabled={!startDate || !endDate}
						onClick={onApply}
					>
						Apply
					</button>
				</div>
			</div>
		</div>
	);
};
